import React, { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../rootReducer';

interface PublicationImageWithLogoProps {
    imageUrl: string;
    alt?: string;
    className?: string;
    isGlobal?: boolean;
    showLogo?: boolean;
    extension?: string;
}

/**
 * Component to display a publication image with user logo overlay using CSS
 *
 * The logo is displayed only if:
 * - The publication is global (created by ROLE_ADMIN)
 * - The user is an SSO user
 * - The user has enabled displayLogoOnPublications option
 * - A logo is available
 */
const PublicationImageWithLogo: React.FC<PublicationImageWithLogoProps> = ({
    imageUrl,
    alt = 'illustration',
    className = '',
    isGlobal = false,
    showLogo = true,
    extension = '',
}) => {
    const isSsoUser = useSelector((state: RootState) => state.user.isSsoUser);
    const userDisplayLogo = useSelector((state: RootState) => state.user.displayLogoOnPublications);
    const userGroupement = useSelector((state: RootState) => state.user.groupement);
    const logoUrl = useSelector((state: RootState) => state.user.logoUrl);

    const containerRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const logoRef = useRef<HTMLImageElement>(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
    const [realImgDimensions, setRealImgDimensions] = useState({ width: 0, height: 0 });
    const [logoOrientation, setLogoOrientation] = useState<'landscape' | 'portrait' | 'square'>(
        'square',
    );

    const hasSpecificGroupement = userGroupement && userGroupement.id !== 1;
    const displayLogoOnPublications = hasSpecificGroupement
        ? userGroupement?.displayLogoOnPublications || false
        : userDisplayLogo;

    const isVideo = extension.toLowerCase() === 'mp4';

    const shouldDisplayLogo =
        showLogo && isGlobal && isSsoUser && displayLogoOnPublications && logoUrl && !isVideo;

    useEffect(() => {
        if (logoUrl && shouldDisplayLogo) {
            const img = new Image();
            img.onload = () => {
                const ratio = img.width / img.height;
                if (ratio > 1.2) {
                    setLogoOrientation('landscape');
                } else if (ratio < 0.8) {
                    setLogoOrientation('portrait');
                } else {
                    setLogoOrientation('square');
                }
            };
            img.src = logoUrl;
        }
    }, [logoUrl, shouldDisplayLogo]);

    useEffect(() => {
        if (imgRef.current && containerRef.current && shouldDisplayLogo) {
            const currentImgRef = imgRef.current;
            const currentContainerRef = containerRef.current;

            const updateDimensions = () => {
                if (currentImgRef && currentContainerRef) {
                    const containerWidth = currentContainerRef.clientWidth;
                    const containerHeight = currentContainerRef.clientHeight;

                    const naturalWidth = currentImgRef.naturalWidth;
                    const naturalHeight = currentImgRef.naturalHeight;

                    const containerRatio = containerWidth / containerHeight;
                    const imageRatio = naturalWidth / naturalHeight;

                    let displayedWidth, displayedHeight;

                    if (imageRatio > containerRatio) {
                        displayedWidth = containerWidth;
                        displayedHeight = containerWidth / imageRatio;
                    } else {
                        displayedHeight = containerHeight;
                        displayedWidth = containerHeight * imageRatio;
                    }

                    setContainerDimensions({
                        width: containerWidth,
                        height: containerHeight,
                    });

                    setRealImgDimensions({
                        width: displayedWidth,
                        height: displayedHeight,
                    });
                }
            };

            if (currentImgRef.complete && currentImgRef.naturalWidth > 0) {
                updateDimensions();
            } else {
                currentImgRef.addEventListener('load', updateDimensions);
            }

            window.addEventListener('resize', updateDimensions);

            return () => {
                window.removeEventListener('resize', updateDimensions);
                currentImgRef.removeEventListener('load', updateDimensions);
            };
        }
    }, [shouldDisplayLogo, imageUrl]);

    return (
        <div
            className={className}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
            }}
        >
            <div
                ref={containerRef}
                style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                }}
            >
                <img
                    ref={imgRef}
                    src={imageUrl}
                    alt={alt}
                    style={{
                        display: 'block',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                    }}
                />
                {shouldDisplayLogo &&
                    realImgDimensions.width > 0 &&
                    (() => {
                        const referenceSize = 1080;
                        const scaleFactor =
                            Math.min(realImgDimensions.width, realImgDimensions.height) /
                            referenceSize;
                        const offset = Math.max(5, 55 * scaleFactor);

                        let maxWidth, maxHeight;
                        if (logoOrientation === 'landscape') {
                            maxWidth = 380 * scaleFactor;
                            maxHeight = 105 * scaleFactor;
                        } else {
                            maxWidth = 200 * scaleFactor;
                            maxHeight = 200 * scaleFactor;
                        }

                        const horizontalOffset =
                            (containerDimensions.width - realImgDimensions.width) / 2;
                        const verticalOffset =
                            (containerDimensions.height - realImgDimensions.height) / 2;

                        return (
                            <img
                                ref={logoRef}
                                src={logoUrl}
                                alt="Logo utilisateur"
                                style={{
                                    position: 'absolute',
                                    top: `${verticalOffset + offset}px`,
                                    right: `${horizontalOffset + offset}px`,
                                    maxWidth: `${maxWidth}px`,
                                    maxHeight: `${maxHeight}px`,
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                }}
                            />
                        );
                    })()}
            </div>
        </div>
    );
};

export default PublicationImageWithLogo;
